// ダッシュボード（design/README.md §1）。KPI4枚・進行状況・要対応・アフィリ経由売上。
import { useEffect, useMemo, useState } from "react";
import type { GfAsset, GfTabProps, Loadable } from "../../../types/gifting";
import { fetchGfAssets } from "../../../api/giftingClient";
import {
  ageDays,
  ageLabel,
  entryAction,
  fmtAsOf,
  fmtMD,
  fmtYen,
  fmtYenShort,
  fmtYmd,
  igDetectCounts,
  igDetectTodoOf,
  isArchived,
  listCandidates,
  STAGE_NAMES,
  typeHasAffiliate,
  typeHasFee,
} from "../../../lib/giftingRules";
import { GfCard, GfEmpty, GfErrorBanner, GfHBar, GfNotConnected, GfSkeleton, GfTag, type GfTone } from "../ui/GfUi";
import { creatorMap, errMsg } from "./kit/helpers";
import { useGfIgDetectedMedia } from "../useGfIgDetect";
import { useGfShipStatus } from "../useGfShipStatus";

interface Todo {
  key: string;
  title: string;
  sub: string;
  tag: string;
  tone: GfTone;
  age: number;
  onClick: () => void;
}

const TONE_ORDER: Record<string, number> = { warn: 0, caution: 1, info: 2 };
/** 配送系（住所・発送）の要対応は発送タブで開く */
const SHIP_LABELS = new Set(["住所を催促", "発送する", "出荷指示", "住所を修正"]);
const MAX_TODOS = 8;

function entryTitle(label: string, name: string): string {
  switch (label) {
    case "打診する":
      return `${name} に打診する`;
    case "催促":
      return `${name} から返信なし`;
    case "条件を確定":
      return `${name} の条件を確定`;
    case "住所を催促":
      return `${name} の住所が未入力`;
    case "発送する":
      return `${name} の発送待ち`;
    case "出荷指示":
      return `${name} の出荷指示待ち`;
    case "住所を修正":
      return `${name} の住所を修正`;
    case "投稿を催促":
      return `${name} の投稿期限を超過`;
    case "投稿を確認":
    case "確認して完了":
      return `${name} の投稿を確認`;
    case "許諾を記録":
      return `${name} の2次利用許諾を記録`;
    default:
      return `${name}：${label}`;
  }
}

export function DashboardTab({ data, nowIso, month, nav, refresh }: GfTabProps) {
  const { board, monthly, payments } = data;
  const campaignsById = useMemo(() => new Map(board.campaigns.map((c) => [c.id, c])), [board.campaigns]);
  const creators = useMemo(() => creatorMap(board.creators), [board.creators]);

  // 素材の期限間近は board に載っていないので別取得（失敗しても他の箇所は出す）
  const [assets, setAssets] = useState<Loadable<GfAsset[]>>({ status: "loading" });
  useEffect(() => {
    let live = true;
    fetchGfAssets("expiring")
      .then((d) => live && setAssets({ status: "ok", data: d }))
      .catch((e: unknown) => live && setAssets({ status: "error", message: errMsg(e) }));
    return () => {
      live = false;
    };
  }, [board]);

  const { status: shipStatus } = useGfShipStatus(board);
  // 第3弾: 検知した投稿（未紐づけ）。取得に失敗しても（旧バックエンドなど）他の要対応は出す
  const { media: detected } = useGfIgDetectedMedia("unlinked", board);
  const detectedList = detected.status === "ok" ? detected.data : [];
  const detectCounts = igDetectCounts(detectedList);

  const live = board.entries.filter((e) => !isArchived(e));

  // ---- KPI ----
  const activeCampaigns = board.campaigns.filter((c) => c.status === "active" && live.some((e) => e.campaign_id === c.id)).length;
  const members = new Set(live.map((e) => e.influencer_id)).size;
  const shippedThisMonth = board.entries.filter(
    (e) => e.status !== "cancelled" && e.shipped_at && fmtYmd(e.shipped_at).slice(0, 7) === month
  ).length;
  const addrWaiting = live.filter((e) => e.stage === 4 && e.status === "waiting_address").length;

  const affKpi =
    monthly.status === "ok"
      ? { value: fmtYenShort(monthly.data.shopify.sales), sub: "Shopify のみ（楽天・Amazon は未接続）" }
      : monthly.status === "loading"
        ? { value: "…", sub: "読み込み中" }
        : { value: "—", sub: "取得できませんでした" };

  const unpaidPersons = payments.status === "ok" ? payments.data.persons.filter((p) => p.unpaid > 0).length : 0;
  const payKpi =
    payments.status === "ok"
      ? { value: fmtYenShort(payments.data.total_unpaid), sub: `${Number(month.slice(5))}月分・未払 ${unpaidPersons}名` }
      : payments.status === "loading"
        ? { value: "…", sub: "読み込み中" }
        : { value: "—", sub: "取得できませんでした" };

  // ---- 進行状況（段階別件数。0=営業リスト） ----
  const candidates = listCandidates(board.creators, board.entries);
  const funnel = STAGE_NAMES.slice(0, 8).map((name, i) => ({
    name,
    count: i === 0 ? candidates.length : live.filter((e) => e.stage === i).length,
  }));
  const funnelMax = Math.max(1, ...funnel.map((f) => f.count));

  // ---- 要対応 ----
  const todos: Todo[] = [];
  for (const e of live) {
    const camp = e.campaign_id ? campaignsById.get(e.campaign_id) ?? null : null;
    const act = entryAction(e, camp, nowIso);
    if (!act) continue;
    const name = creators.get(e.influencer_id)?.name ?? "（不明）";
    const age = ageDays(e, nowIso);
    todos.push({
      key: `e-${e.id}`,
      title: entryTitle(act.label, name),
      sub: `${camp?.name ?? e.product_name}・${ageLabel(age)}`,
      tag: act.label,
      tone: act.tone === "ok" ? "info" : act.tone,
      age,
      onClick: () => nav.openEntry(e.id, SHIP_LABELS.has(act.label) ? "ship" : "deals"),
    });
  }
  // 口座未登録（有償/アフィリ案件で投稿確認以降に進んだクリエイター）
  const payeeSeen = new Set<string>();
  for (const e of board.entries) {
    if (e.stage < 6 || e.status === "cancelled") continue;
    const camp = e.campaign_id ? campaignsById.get(e.campaign_id) ?? null : null;
    const needsPay = e.deal_type === "paid" || (camp ? typeHasFee(camp.type) || typeHasAffiliate(camp.type) : false);
    const cr = creators.get(e.influencer_id);
    if (!needsPay || !cr || cr.payee_status === "registered" || payeeSeen.has(cr.id)) continue;
    payeeSeen.add(cr.id);
    todos.push({
      key: `p-${cr.id}`,
      title: `${cr.name} の口座が未登録`,
      sub: camp?.name ?? e.product_name,
      tag: "口座",
      tone: "info",
      age: 0,
      onClick: () => nav.openCreator(cr.id),
    });
  }
  // 素材の許諾期限
  if (assets.status === "ok") {
    for (const a of assets.data) {
      todos.push({
        key: `a-${a.id}`,
        title: `${a.influencer_name} の素材の許諾期限`,
        sub: a.reuse_until ? `${fmtMD(a.reuse_until)} まで` : "期限間近",
        tag: "素材",
        tone: "caution",
        age: 0,
        onClick: () => nav.set({ tab: "assets", asset: "expiring" }),
      });
    }
  }
  // 第2弾: 出荷指示の送信失敗（直近7日）・同期エラー
  if (shipStatus.status === "ok" && (shipStatus.data.failed > 0 || shipStatus.data.last_error)) {
    const s = shipStatus.data;
    todos.push({
      key: "ship-failed",
      title: s.failed > 0 ? `出荷指示の送信失敗 ${s.failed}件` : "追跡番号の同期でエラー",
      sub: s.last_error ? `直近のエラー: ${s.last_error}` : "発送タブで状態を確認して再送してください",
      tag: "出荷",
      tone: "warn",
      age: 0,
      onClick: () => nav.set({ tab: "ship" }),
    });
  }
  // 第3弾: 検知した投稿の確認（クリエイター登録済みで案件に紐づいていないものだけ。未登録アカウントは営業候補として件数を添える）
  const detectTodo = igDetectTodoOf(detectedList);
  if (detectTodo) {
    todos.push({
      key: "ig-detect",
      title: detectTodo.title,
      sub: detectTodo.sub,
      tag: "投稿検知",
      tone: "caution",
      age: 0,
      onClick: () => nav.set({ tab: "assets" }),
    });
  }
  todos.sort((a, b) => (TONE_ORDER[a.tone] ?? 3) - (TONE_ORDER[b.tone] ?? 3) || b.age - a.age);
  const shownTodos = todos.slice(0, MAX_TODOS);

  const kpis: Array<{ label: string; value: string; sub: string }> = [
    { label: "進行中の案件", value: String(activeCampaigns), sub: `参加クリエイター ${members}名` },
    { label: "今月の発送", value: String(shippedThisMonth), sub: `住所待ち ${addrWaiting}件` },
    { label: "アフィリ経由売上", value: affKpi.value, sub: affKpi.sub },
    { label: "今月の支払予定", value: payKpi.value, sub: payKpi.sub },
  ];

  return (
    <>
      <div className="gf-page-head">
        <h1 className="gf-h1">ダッシュボード</h1>
        <span className="gf-asof">{fmtAsOf(nowIso)}</span>
      </div>

      <div className="gf-grid-kpi">
        {kpis.map((k) => (
          <GfCard key={k.label} className="gf-kpi">
            <span className="gf-kpi-label">{k.label}</span>
            <span className="gf-kpi-value gf-kpi-value-tight">{k.value}</span>
            <span className="gf-kpi-sub">{k.sub}</span>
          </GfCard>
        ))}
      </div>

      <div className="gf-grid-2 is-start">
        <GfCard className="gf-c18 gf-col-card">
          <div className="gf-card-head-row">
            <h2 className="gf-h2">進行状況</h2>
            <button type="button" className="gf-link-btn" onClick={() => nav.set({ tab: "deals" })}>
              パイプラインを開く
            </button>
          </div>
          {funnel.map((f) => (
            <div className="gf-funnel-row" key={f.name}>
              <span className="gf-funnel-name">{f.name}</span>
              <GfHBar pct={(f.count / funnelMax) * 100} />
              <span className="gf-funnel-count">{f.count}</span>
            </div>
          ))}
        </GfCard>

        <GfCard className="gf-c18 gf-col-card is-tight">
          <h2 className="gf-h2 gf-h2-gap">要対応</h2>
          {shownTodos.length === 0 ? (
            <GfEmpty>要対応はありません</GfEmpty>
          ) : (
            shownTodos.map((t) => (
              <button type="button" className="gf-todo" key={t.key} onClick={t.onClick}>
                <span className="gf-todo-main">
                  <span className="gf-todo-title">{t.title}</span>
                  <span className="gf-todo-sub">{t.sub}</span>
                </span>
                <GfTag tone={t.tone}>{t.tag}</GfTag>
              </button>
            ))
          )}
          {todos.length > shownTodos.length && (
            <div className="gf-note gf-todo-more">ほか {todos.length - shownTodos.length}件（案件・発送タブで確認）</div>
          )}
          {assets.status === "error" && <div className="gf-note gf-todo-more">素材の期限は取得できませんでした（{assets.message}）</div>}
          {!detectTodo && detectCounts.sales > 0 && (
            <div className="gf-note gf-todo-more">営業候補: 未登録アカウントのタグ付け投稿 {detectCounts.sales}件（素材庫で確認）</div>
          )}
        </GfCard>

        <GfCard className="gf-c18 gf-col-card">
          <h2 className="gf-h2">アフィリ経由売上（今月）</h2>
          {monthly.status === "loading" && (
            <>
              <GfSkeleton height={14} />
              <GfSkeleton height={8} />
            </>
          )}
          {monthly.status === "error" && <GfErrorBanner message={`アフィリ売上を取得できませんでした（${monthly.message}）`} onRetry={() => void refresh()} />}
          {monthly.status === "ok" && (
            <div className="gf-bar-row">
              <div className="gf-bar-head">
                <span>Shopify</span>
                <span className="gf-mono">{fmtYen(monthly.data.shopify.sales)}</span>
              </div>
              <GfHBar sub pct={monthly.data.shopify.sales > 0 ? 100 : 0} />
              <span className="gf-note">
                成果 {monthly.data.shopify.conversions}件・報酬見込 {fmtYen(monthly.data.shopify.commission)}
              </span>
            </div>
          )}
          <GfNotConnected title="楽天市場" note="売上計測は未接続です（クリック計測のみ）" />
          <GfNotConnected title="Amazon" note="売上計測は未接続です（クリック計測のみ）" />
        </GfCard>
      </div>
    </>
  );
}
